import { ASPECT_RATIOS, AspectRatioKey, DesignChannelMarketplace, DesignChannels } from 'components/NewFileModal/Types'
import { useModals } from 'almanac-ui/Modal'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateDesign as useCreateDesignMutation } from 'hooks/queries/workspaces/useCreateDesign'
import { designSchemas, videoSchemaOverrides } from 'components/Wizards/schemas/schemas'
import { DesignWizard } from 'components/Wizards/DesignWizard/DesignWizard'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import { useBrandVisualKits } from 'hooks/queries/workspaces/useBrandVisualKits'
import { compact } from 'lodash'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useBrandVoices } from 'hooks/useBrandVoices'
import { AlmanacFile, AlmanacFileSummary } from 'types'
import { Data } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'
import { DesignMarketplaceWizard } from 'components/Wizards/DesignMarketplaceWizard/DesignMarketplaceWizard'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useIsNewWizardFlowEnabled } from 'components/Wizards/MobileWizards/hooks/useIsNewWizardFlowEnabled'
import { useRootStore } from 'stores/RootStore'
import { MobileWizardsDrawer } from 'components/Wizards/MobileWizards/MobileWizardsDrawer/MobileWizardsDrawer'

interface Props {
  schemaModifier?: (schema: MultiStepSchema) => MultiStepSchema
  onSuccess?: (data: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> } | Data) => void
}

const useOpenDesignWizard = (props: Props = {}) => {
  const { schemaModifier = (schema) => schema, onSuccess } = props
  const { openModal } = useModals()
  const videoWizardsEnabled = useFlipperFlag('video_wizards')
  const { data: sourceMaterials } = useSourceMaterials()
  const brandVisualKits = useBrandVisualKits({
    staleTime: 10 * 60 * 1000, // 10 minutes
  }).data?.brand_visual_kits
  const currentWorkspace = useCurrentWorkspace()
  const { data: voices } = useBrandVoices(currentWorkspace)
  const { data: brandKits } = useBrandKits()
  const { isMobile } = useResponsiveDesign()
  const { isNewWizardFlowEnabled } = useIsNewWizardFlowEnabled()
  const { drawersStore } = useRootStore()

  const defaultValues = {
    source_material_ids: compact([sourceMaterials?.[0]?.id, sourceMaterials?.[1]?.id]),
    brand_visual_kit_id: brandKits ? undefined : brandVisualKits?.[brandVisualKits.length - 1]?.id,
    brand_voice_id: brandKits ? undefined : voices?.[voices.length - 1]?.id,
    brand_kit_id: brandKits?.[brandKits.length - 1]?.id,
  }

  return ({ aspectRatioKey, initialValues, ...rest }: { aspectRatioKey: AspectRatioKey; initialValues?: {} }) => {
    const channel = DesignChannels[aspectRatioKey]
    const isMarketplaceWizard = Object.values(DesignChannelMarketplace).includes(
      channel as string as DesignChannelMarketplace,
    )

    const wizardValues = {
      width: ASPECT_RATIOS[aspectRatioKey].width,
      height: ASPECT_RATIOS[aspectRatioKey].height,
      ...defaultValues,
      ...initialValues,
    }

    if (isMobile && isNewWizardFlowEnabled(channel)) {
      return drawersStore.openDrawer(MobileWizardsDrawer, { channel, initialValues: { ...wizardValues }, ...rest })
    }

    if (isMarketplaceWizard) {
      return openModal(DesignMarketplaceWizard, {
        onSuccess,
        schema: schemaModifier(designSchemas[channel]),
        initialValues: { ...wizardValues },
        ...rest,
      })
    }

    let schemas = designSchemas
    if (videoWizardsEnabled && !isMobile) schemas = { ...schemas, ...videoSchemaOverrides }

    return openModal(DesignWizard, {
      schema: schemaModifier(schemas[channel]),
      onSuccess,
      initialValues: { ...wizardValues },
      ...rest,
    })
  }
}

export const useCreateDesign = (props?: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync, ...rest } = useCreateDesignMutation({ workspaceId: workspaceId })
  const openDesignWizard = useOpenDesignWizard(props)

  return {
    createDesign: openDesignWizard,
    mutateAsync,
    ...rest,
  }
}
